import { BlockList, PlacementProvider } from '@zillow/cms-blocks';
import { Box, Image, Spacer } from '@zillow/constellation';
import { withPreload } from '@zillow/zrs-cms';
import Script from 'next/script';
import React from 'react';

import ArticleAuthor from '../ArticleAuthor/ArticleAuthor';
import FooterTemplate from '../FooterTemplate/FooterTemplate';
import RelatedPosts from '../RelatedPosts/RelatedPosts';
import Tags from '../Tags/Tags';

import {
    B2CPostTemplateProps,
    SidebarWrapperProps,
} from './B2CPostTemplate.types';
import Hero from './Hero/Hero';

const ImagePreload = withPreload(Image);

export const SidebarWrapper: React.FC<SidebarWrapperProps> = ({
    children,
    sticky = false,
    top = '$spacing.md',
}) => (
    <Box
        sx={{
            ...(sticky
                ? {
                      position: 'sticky',
                      top: top,
                      alignSelf: 'flex-start',
                  }
                : {}),
        }}
    >
        {children}
    </Box>
);

const B2CPostTemplate: React.FC<B2CPostTemplateProps> = ({
    post,
    contentWidth = '$breakpoints.xl',
    gapSpacing = '$spacing.xl',
    stickySidebarTop,
    knotchAccountId,
}) => {
    const sidebar = (
        <React.Fragment>
            <ArticleAuthor
                avatar={
                    post?.author?.avatar ? (
                        <ImagePreload
                            src={post.author.avatar}
                            alt={post.author.name}
                        />
                    ) : undefined
                }
                name={post.author?.name}
                jobTitle={post.author?.jobTitle}
                publishDate={post.date || ''}
            />
            <Spacer marginBottom="md" />
        </React.Fragment>
    );

    const postSidebar = post?.sidebar ? (
        <BlockList blocks={post.sidebar.blocks || []} />
    ) : null;

    return (
        <React.Fragment>
            <Box
                sx={{
                    margin: '0 auto',
                    maxWidth: contentWidth,
                    padding: '$spacing.xl $spacing.sm',
                }}
            >
                <Box
                    sx={{
                        marginBottom: '$spacing.xs',
                        '@media $md': {
                            marginBottom: '$spacing.md',
                        },
                        '@media $xl': {
                            marginBottom: '$spacing.xl',
                        },
                    }}
                >
                    <Hero post={post} />
                </Box>

                <Box
                    sx={{
                        '@media $lg': {
                            display: 'flex',
                        },
                    }}
                >
                    <Box
                        sx={{
                            flex: '1 1 0',
                            minWidth: 0,
                            marginBottom: '$spacing.lg',
                        }}
                    >
                        <PlacementProvider placement="post">
                            <BlockList blocks={post.blocks || []} />
                        </PlacementProvider>
                        {!!knotchAccountId && (
                            <>
                                <div className="knotch_placeholder" />
                                <Script
                                    src="https://www.knotch-cdn.com/unit/latest/knotch.min.js"
                                    data-account={knotchAccountId}
                                />
                            </>
                        )}
                        <Tags postTags={post.postTags} />
                    </Box>
                    <Box
                        sx={{
                            marginTop: '$spacing.xl',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            width: '300px',
                            '@media $md_lte': {
                                width: '100%',
                                marginTop: 0,
                            },
                            '@media $lg': {
                                marginLeft: gapSpacing,
                                marginRight: 0,
                                marginTop: 0,
                            },
                        }}
                    >
                        {sidebar && <SidebarWrapper>{sidebar}</SidebarWrapper>}
                        {postSidebar && (
                            <SidebarWrapper sticky top={stickySidebarTop}>
                                {postSidebar}
                            </SidebarWrapper>
                        )}
                    </Box>
                </Box>
            </Box>

            {(post.footer || post.relatedPosts) && (
                <FooterTemplate
                    footer={post.footer}
                    relatedArticles={
                        post.relatedPosts && (
                            <RelatedPosts
                                post={{
                                    ...post,
                                    id: parseInt(post.id),
                                    relatedPosts: post.relatedPosts.map(
                                        (p) => ({
                                            ...p,
                                            id: parseInt(p.id),
                                        })
                                    ),
                                }}
                            />
                        )
                    }
                />
            )}
        </React.Fragment>
    );
};

export default B2CPostTemplate;
